/**
 * Staff rename a report, show or hide it, configure what it shows (RP5) and rebind its data
 * (RP8): one drawer, one save. Rebinds go first, one request per changed slot, then the patch.
 */
import { useState } from 'react';
import { Button, Drawer, Group, Stack, Switch, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useProjectObjects } from '@/api/objects';
import { usePatchReport, useRebindReport, useReportKinds, type Report, type ReportPatch } from '@/api/reports';
import { ReportInputsFields, selectionOf } from './ReportInputsFields';
import { SchemaForm, type JsonSchema } from './options/SchemaForm';
import { optionErrors } from './options/schemaPath';

/** Its clicks never bubble (the drawer is a portal, but React events still climb to a table row's handler). */
export function ReportSettingsDrawer({ report }: { report: Report }) {
  const { t } = useTranslation('reports');
  const kinds = useReportKinds();
  const patch = usePatchReport(report.public_id);
  const rebind = useRebindReport(report.public_id);
  const objects = useProjectObjects(report.project);
  const [opened, setOpened] = useState(false);
  const [inputs, setInputs] = useState<Record<string, string | null>>(() => selectionOf(report.inputs ?? []));
  const [value, setValue] = useState<Record<string, unknown>>((report.options ?? {}) as Record<string, unknown>);
  const [name, setName] = useState(report.name ?? '');
  const [visible, setVisible] = useState(report.visible_to_client);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const info = kinds.data?.find((k) => k.key === report.kind);
  const hasOptions = !!info && Object.keys((info.options_schema as JsonSchema).properties ?? {}).length > 0;

  const open = () => {
    setValue((report.options ?? {}) as Record<string, unknown>);
    setName(report.name ?? '');
    setVisible(report.visible_to_client);
    setInputs(selectionOf(report.inputs ?? []));
    setErrors({});
    setOpened(true);
  };
  const save = () => {
    const changes: ReportPatch = {};
    const newName = name.trim();
    if (newName !== (report.name ?? '')) changes.name = newName;
    if (visible !== report.visible_to_client) changes.visible_to_client = visible;
    if (hasOptions && JSON.stringify(value) !== JSON.stringify(report.options ?? {})) changes.options = value;
    const was = selectionOf(report.inputs ?? []);
    const rebinds = (info?.slots ?? []).map((s) => s.role).filter((role) => (inputs[role] ?? null) !== (was[role] ?? null));
    if (!Object.keys(changes).length && !rebinds.length) {
      setOpened(false);
      return;
    }
    const apply = async () => {
      for (const role of rebinds) await rebind.mutateAsync({ role, object: inputs[role] ?? null });
      if (Object.keys(changes).length) await patch.mutateAsync(changes);
    };
    apply()
      .then(() => {
        notifications.show({ color: 'forest', message: t('settings.saved') });
        setOpened(false);
      })
      .catch((e: unknown) => {
        const byPath = optionErrors(e);
        setErrors(byPath);
        if (!Object.keys(byPath).length) notifications.show({ color: 'red', message: errorMessage(e) });
      });
  };

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Button
        size="xs"
        variant="default"
        className="report-settings"
        onClick={open}
      >
        {t('settings.open')}
      </Button>
      <Drawer opened={opened} onClose={() => setOpened(false)} title={t('settings.title')} position="right" size="md">
        <Stack gap="md">
          <TextInput label={t('settings.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Switch label={t('table.visible')} checked={visible} onChange={(e) => setVisible(e.currentTarget.checked)} />
          {hasOptions && info && (
            <SchemaForm kind={report.kind} schema={info.options_schema as JsonSchema} value={value} onChange={setValue} errors={errors} />
          )}
          <ReportInputsFields
            slots={info?.slots ?? []}
            objects={objects.data ?? []}
            current={report.inputs ?? []}
            value={inputs}
            onChange={(role, object) => setInputs((prev) => ({ ...prev, [role]: object }))}
            disabled={patch.isPending || rebind.isPending}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpened(false)}>
              {t('settings.cancel')}
            </Button>
            <Button onClick={save} loading={patch.isPending || rebind.isPending}>
              {t('settings.save')}
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </span>
  );
}
